import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Briefcase, Shield, AlertCircle, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import '../../styles/auth-mobile.css';

type LoginContext = 'client' | 'staff' | 'admin';

interface LoginPageProps {
  context: LoginContext;
}

export const LoginPage: React.FC<LoginPageProps> = ({ context }) => {
  const { user, session, login, isLoading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [hasRedirected, setHasRedirected] = useState(false);

  const contextConfig = {
    client: {
      title: 'Client Portal',
      subtitle: 'Access your health journey',
      icon: User,
      color: 'var(--accent-blue)'
    },
    staff: {
      title: 'Staff Dashboard',
      subtitle: 'Manage your clients',
      icon: Briefcase,
      color: 'var(--accent-green)'
    },
    admin: {
      title: 'Admin Panel',
      subtitle: 'System administration',
      icon: Shield,
      color: 'var(--accent-red)'
    }
  };

  const config = contextConfig[context];
  const IconComponent = config.icon;

  // Redirect if already logged in
  useEffect(() => {
    if (user && session && !hasRedirected) {
      // Redirect to the appropriate context landing page
      // Client context lands on /home, admin/staff land on /dashboard
      const landingPage = session.context === 'client' ? 'home' : 'dashboard';
      const contextPath = `/${session.context}/${landingPage}`;
      setHasRedirected(true);
      navigate(contextPath);
    }
  }, [user, session?.context, navigate, hasRedirected]);

  // Release the global 768px min-width floor for the auth pages below 1024px
  // so the centered login card fits a phone viewport. See styles/auth-mobile.css.
  useEffect(() => {
    document.documentElement.classList.add('ctx-auth');
    return () => document.documentElement.classList.remove('ctx-auth');
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }

    try {
      // Context is implicit from the route - /client, /staff, /admin
      const result = await login(email, password, context);
      
      if (!result.success) {
        setError(result.message || 'Invalid credentials');
      }
      // Don't manually redirect on success - let useEffect handle context-aware redirect
    } catch (err) {
      setError('Login failed. Please try again.');
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <div className="login-icon" style={{ backgroundColor: config.color }}>
            <IconComponent size={32} color="white" />
          </div>
          <h1 className="login-title">Mission Control</h1>
          <h2 className="login-subtitle">{config.title}</h2>
          <p className="login-description">{config.subtitle}</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="email" className="form-label">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="form-input"
              placeholder="Enter your email"
              disabled={isLoading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password" className="form-label">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="form-input"
              placeholder="Enter your password"
              disabled={isLoading}
              autoComplete="current-password"
            />
          </div>

          {error && (
            <div className="error-message">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          <button 
            type="submit" 
            className="login-button"
            disabled={isLoading}
            style={{ backgroundColor: config.color }}
          >
            {isLoading ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                <span>Signing in...</span>
              </>
            ) : (
              <span>Sign In</span>
            )}
          </button>
        </form>
      </div>

      <style>{`
        .login-container {
          min-height: 100vh;
          background: linear-gradient(135deg, var(--bg-secondary) 0%, var(--bg-primary) 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }

        .login-card {
          background: var(--bg-primary);
          border-radius: var(--radius-large);
          box-shadow: var(--shadow-heavy);
          padding: 40px;
          width: 100%;
          max-width: 400px;
          border: 1px solid var(--border-secondary);
        }

        .login-header {
          text-align: center;
          margin-bottom: 32px;
        }

        .login-icon {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 20px;
        }

        .login-title {
          font-size: 24px;
          font-weight: 700;
          color: var(--text-primary);
          margin-bottom: 4px;
        }

        .login-subtitle {
          font-size: 18px;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 8px;
        }

        .login-description {
          font-size: 14px;
          color: var(--text-tertiary);
          margin: 0;
        }

        .login-form {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .form-label {
          font-size: 14px;
          font-weight: 600;
          color: var(--text-primary);
        }

        .form-input {
          padding: 12px 16px;
          border: 1px solid var(--border-secondary);
          border-radius: var(--radius-medium);
          background: var(--bg-secondary);
          color: var(--text-primary);
          font-size: 14px;
          transition: all 0.2s ease;
        }

        .form-input:focus {
          outline: none;
          border-color: var(--accent-blue);
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .form-input:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .login-button {
          padding: 16px 24px;
          border: none;
          border-radius: var(--radius-medium);
          font-weight: 600;
          color: white;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          font-size: 16px;
        }

        .login-button:hover:not(:disabled) {
          opacity: 0.9;
          transform: translateY(-1px);
        }

        .login-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .error-message {
          display: flex;
          align-items: center;
          gap: 8px;
          color: var(--accent-red);
          font-size: 14px;
          padding: 12px 16px;
          background: rgba(255, 59, 48, 0.1);
          border: 1px solid rgba(255, 59, 48, 0.2);
          border-radius: var(--radius-medium);
        }

        .animate-spin {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
};